import React, { useRef, useEffect } from "react";
import { FileSpreadsheet, X } from "lucide-react";

interface Attachment {
  name: string;
  size: number;
  url: string;
}

interface ViewNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  note: {
    coachName: string;
    createdAt: string;
    sessionOverview?: string;
    topicsDiscussed?: string[];
    externalLinks?: string[];
    attachments?: Attachment[];
  };
}

const ViewNoteModal: React.FC<ViewNoteModalProps> = ({ isOpen, onClose, note }) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // ESC key close
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  if (!isOpen) return null;

  const handleClickOutside = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === modalRef.current) onClose();
  };

  return (
    <div
      ref={modalRef}
      onClick={handleClickOutside}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white rounded-2xl shadow-lg max-w-5xl w-full mx-4 p-10 overflow-y-auto max-h-[90vh] scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >

        {/* Date */}
        <div className="text-center mb-4 text-primary900 graySmallText font-medium">
          {new Date(note.createdAt).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </div>

        {/* Coach Name */}
          <div className=" textBodySerif mb-6 text-center">
            {note.coachName}
          </div>

        {/* External Links */}
        {note.externalLinks && note.externalLinks.length > 0 && (
          <div className="mb-6">
            <div className="text-gray-500 graySmallText font-medium mb-2">
              External links
            </div>
            <div className="flex flex-wrap gap-2">
              {note.externalLinks.map((link, idx) => (
                <a
                  key={idx}
                  href={link.startsWith("http") ? link : `https://${link}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-icyBlue text-blue-600 text2 px-4 py-1 rounded-full border-[.5px] border-paleBlue transition font-thin"
                >
                  {link}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Session Overview */}
        {note.sessionOverview && (
          <div className="mb-[120px]">
            <div className="text-gray-500 graySmallText font-medium mb-2">
              Session overview
            </div>
            <div className="text-primary900 text2 font-normal whitespace-pre-line">
              {note.sessionOverview}
            </div>
          </div>
        )}

        {/* Attachments */}
        {note.attachments && note.attachments.length > 0 && (
          <div className="mt-8 mb-8">
            {note.attachments.map((file, idx) => (
              <a
                key={idx}
                href={file.url}
                download={file.name}
                className="flex items-center gap-3 border border-gray-200 rounded-xl p-4 hover:bg-gray-50 transition"
              >
                <div className="flex items-center justify-center bg-green-50 border border-green-100 p-2 rounded-lg">
                  <FileSpreadsheet className="text-green-600 w-6 h-6" />
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-800">
                    {file.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {(file.size / 1024).toFixed(1)} KB – 100% uploaded
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}

        {/* Topic Labels */}
        {note.topicsDiscussed && note.topicsDiscussed.length > 0 && (
          <div className="flex gap-2 flex-wrap mt-2">
            {note.topicsDiscussed.map((topic, idx) => (
              <span
                key={idx}
                className="border border-gray-200 rounded-full px-3 py-1 text5 text-gray-600 bg-white"
              >
                {topic}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewNoteModal;
